import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { CharacterMention, CollectionItem } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { Modal } from "../components/Modal.js";
import { CharacterAvatar } from "../components/CharacterAvatar.js";
import { PortraitControl } from "./PortraitControl.js";
import type { CollectionConfig, FieldSpec } from "./config.js";

export interface CollectionRefs {
  scenes: { id: string; title: string }[];
  locations: { id: string; name: string }[];
}

interface Props {
  projectId: string;
  config: CollectionConfig;
  refs: CollectionRefs;
  onOpenScene: (sceneId: string) => void;
  /** Preselect this item (e.g. arriving from global search). */
  focusId?: string | null;
}

export function CollectionPanel({ projectId, config, refs, onOpenScene, focusId }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<CollectionItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<CollectionItem | null>(null);
  const [selected2, setSelected2] = useState<Set<string>>(new Set());
  const [showMerge, setShowMerge] = useState(false);
  const [findingDupes, setFindingDupes] = useState(false);

  const load = useCallback(async () => {
    const { items: list } = await api.listCollection(projectId, config.path);
    setItems(list);
    setSelectedId((current) => current ?? focusId ?? list[0]?.id ?? null);
  }, [projectId, config.path, focusId]);

  useEffect(() => {
    setItems(null);
    setSelectedId(null);
    setQuery("");
    load().catch((err) => toast.error(err instanceof Error ? err.message : "Failed to load"));
  }, [load, toast]);

  // Deep-link to a specific item when arriving from search.
  useEffect(() => {
    if (focusId) setSelectedId(focusId);
  }, [focusId]);

  const titleOf = (item: CollectionItem) => (item[config.titleField] as string) || "(untitled)";

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => titleOf(i).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query]);

  const selected = items?.find((i) => i.id === selectedId) ?? null;

  const create = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, config.path, config.newItem);
      setItems((prev) => (prev ? [...prev, item] : [item]));
      setSelectedId(item.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create");
    }
  };

  const onItemChanged = (updated: CollectionItem) =>
    setItems((prev) => (prev ? prev.map((i) => (i.id === updated.id ? updated : i)) : prev));

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.deleteCollectionItem(config.path, id);
      setItems((prev) => {
        const next = prev ? prev.filter((i) => i.id !== id) : prev;
        if (selectedId === id) setSelectedId(next?.[0]?.id ?? null);
        return next;
      });
      toast.success(`${config.singular} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const move = async (item: CollectionItem, dir: -1 | 1) => {
    if (!items) return;
    const ordered = [...items];
    const idx = ordered.findIndex((i) => i.id === item.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= ordered.length) return;
    [ordered[idx], ordered[swap]] = [ordered[swap]!, ordered[idx]!];
    setItems(ordered);
    try {
      await api.reorderTimeline(projectId, ordered.map((i) => i.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reorder");
      void load();
    }
  };

  // ---- Merge duplicates ----
  const toggleSelect = (id: string) =>
    setSelected2((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const findDuplicates = async () => {
    setFindingDupes(true);
    try {
      const { groups } = await api.duplicateCandidates(projectId, config.path);
      if (groups.length === 0) {
        toast.info("No likely duplicates found.");
        return;
      }
      // Pre-select the first suggested group and open the merge dialog.
      const first = groups[0]!;
      setSelected2(new Set(first.items.map((i) => i.id)));
      setShowMerge(true);
      if (groups.length > 1) {
        toast.info(`Found ${groups.length} possible duplicate groups — merge this one, then run it again.`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to scan");
    } finally {
      setFindingDupes(false);
    }
  };

  const selectedItems = (items ?? []).filter((i) => selected2.has(i.id));

  const doMerge = async (keepId: string) => {
    const mergeIds = [...selected2].filter((id) => id !== keepId);
    if (mergeIds.length === 0) return;
    try {
      await api.mergeCollection(projectId, config.path, keepId, mergeIds);
      toast.success(`Merged ${mergeIds.length} duplicate${mergeIds.length === 1 ? "" : "s"}.`);
      setShowMerge(false);
      setSelected2(new Set());
      setSelectedId(keepId);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Merge failed");
    }
  };

  return (
    <div className="collection">
      <div className="collection-list">
        <div className="collection-list-head">
          <div className="collection-search">
            <Icon name="search" size={14} />
            <input
              className="collection-search-input"
              placeholder={`Search ${config.label.toLowerCase()}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-icon btn-primary btn-sm" title={`New ${config.singular}`} onClick={create}>
            <Icon name="plus" size={15} />
          </button>
        </div>
        {items && items.length > 1 && (
          <div className="collection-subbar">
            {selected2.size >= 2 ? (
              <>
                <button className="btn btn-sm btn-primary" onClick={() => setShowMerge(true)}>
                  <Icon name="layers" size={13} /> Merge {selected2.size}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => setSelected2(new Set())}>Clear</button>
              </>
            ) : (
              <button className="btn btn-sm btn-ghost" onClick={findDuplicates} disabled={findingDupes}>
                <Icon name={findingDupes ? "clock" : "search"} size={13} className={findingDupes ? "spin" : ""} /> Find duplicates
              </button>
            )}
            {selected2.size === 1 && <span className="faint" style={{ fontSize: 11 }}>Select 2+ to merge</span>}
          </div>
        )}
        <div className="collection-items">
          {items === null ? (
            <div className="collection-loading"><Icon name="clock" size={16} className="spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="collection-empty-list">
              <p className="muted">{query ? "No matches." : `No ${config.label.toLowerCase()} yet.`}</p>
              {!query && (
                <button className="btn btn-sm" onClick={create}>
                  <Icon name="plus" size={14} /> New {config.singular}
                </button>
              )}
            </div>
          ) : (
            filtered.map((item, index) => (
              <div
                key={item.id}
                className={`collection-item ${item.id === selectedId ? "active" : ""} ${selected2.has(item.id) ? "checked" : ""}`}
              >
                <input
                  type="checkbox"
                  className="collection-check"
                  checked={selected2.has(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  title="Select to merge"
                />
                <button className="collection-item-main" onClick={() => setSelectedId(item.id)}>
                  {config.showImage ? (
                    <CharacterAvatar name={titleOf(item)} imageUrl={item.imageData as string | null | undefined} size="sm" />
                  ) : (
                    <Icon name={config.icon} size={14} className="collection-item-icon" />
                  )}
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="truncate strong" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="truncate">{titleOf(item)}</span>
                      {item.origin === "ai" && (
                        <span
                          title={`AI-generated${item.model ? ` by ${String(item.model)}` : ""}${item.sourceLabel ? ` · ${String(item.sourceLabel)}` : ""}${item.confidence ? ` · ${String(item.confidence)} confidence` : ""}`}
                          style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 999, background: "var(--surface-3, rgba(127,127,127,0.18))", color: "var(--muted,#888)" }}
                        >
                          AI
                        </span>
                      )}
                    </div>
                    {config.subtitleField && item[config.subtitleField] ? (
                      <div className="truncate faint" style={{ fontSize: 11 }}>
                        {String(item[config.subtitleField])}
                      </div>
                    ) : null}
                  </div>
                </button>
                {config.reorderable && !query && (
                  <span className="collection-reorder">
                    <span onClick={() => void move(item, -1)} title="Move up"
                      style={{ opacity: index === 0 ? 0.3 : 1 }}>
                      <Icon name="chevronRight" size={12} style={{ transform: "rotate(-90deg)" }} />
                    </span>
                    <span onClick={() => void move(item, 1)} title="Move down"
                      style={{ opacity: index === filtered.length - 1 ? 0.3 : 1 }}>
                      <Icon name="chevronRight" size={12} style={{ transform: "rotate(90deg)" }} />
                    </span>
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="collection-detail">
        {selected ? (
          <DetailEditor
            key={selected.id}
            item={selected}
            config={config}
            refs={refs}
            onChanged={onItemChanged}
            onDelete={() => setPendingDelete(selected)}
            onOpenScene={onOpenScene}
          />
        ) : (
          <div className="collection-detail-empty">
            <Icon name={config.icon} size={40} style={{ color: "var(--text-faint)" }} />
            <h2>{config.label}</h2>
            <p className="muted">Select {config.singular === "Entry" ? "an entry" : `a ${config.singular.toLowerCase()}`}, or create one.</p>
            <button className="btn btn-primary" onClick={create}>
              <Icon name="plus" size={15} /> New {config.singular}
            </button>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete ${config.singular.toLowerCase()}`}
          message={`Delete “${titleOf(pendingDelete)}”? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {showMerge && selectedItems.length >= 2 && (
        <MergeDialog
          items={selectedItems}
          config={config}
          titleOf={titleOf}
          onCancel={() => setShowMerge(false)}
          onMerge={doMerge}
        />
      )}
    </div>
  );
}

function MergeDialog({
  items,
  config,
  titleOf,
  onCancel,
  onMerge
}: {
  items: CollectionItem[];
  config: CollectionConfig;
  titleOf: (item: CollectionItem) => string;
  onCancel: () => void;
  onMerge: (keepId: string) => void;
}) {
  // Default keeper = the entry with the most filled-in fields.
  const filledCount = (item: CollectionItem) =>
    config.fields.filter((f) => {
      const v = item[f.key];
      return typeof v === "string" ? v.trim().length > 0 : v != null && v !== "";
    }).length;
  const [keepId, setKeepId] = useState<string>(
    () => [...items].sort((a, b) => filledCount(b) - filledCount(a))[0]!.id
  );

  return (
    <Modal
      title={`Merge ${items.length} ${config.label.toLowerCase()}`}
      subtitle="Choose the entry to keep. The others merge into it (filling only its empty fields) and are deleted."
      onClose={onCancel}
      width={520}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onMerge(keepId)}>
            <Icon name="layers" size={15} /> Merge into selected
          </button>
        </>
      }
    >
      <div className="merge-list">
        {items.map((item) => (
          <label key={item.id} className={`merge-option ${item.id === keepId ? "keep" : ""}`}>
            <input type="radio" name="merge-keep" checked={item.id === keepId} onChange={() => setKeepId(item.id)} />
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="strong truncate">{titleOf(item)}</div>
              <div className="faint" style={{ fontSize: 11 }}>
                {filledCount(item)} field{filledCount(item) === 1 ? "" : "s"} filled
                {item.id === keepId ? " · keep this one" : ""}
              </div>
            </div>
          </label>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11.5, marginTop: 10, marginBottom: 0 }}>
        Your manuscript is never changed. References (POV character, scene location) are re-pointed to the kept entry.
      </p>
    </Modal>
  );
}

export function DetailEditor({
  item,
  config,
  refs,
  onChanged,
  onDelete,
  onOpenScene
}: {
  item: CollectionItem;
  config: CollectionConfig;
  refs: CollectionRefs;
  onChanged: (item: CollectionItem) => void;
  onDelete: () => void;
  onOpenScene: (sceneId: string) => void;
}) {
  const toast = useToast();
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    initial[config.titleField] = (item[config.titleField] as string) ?? "";
    for (const f of config.fields) initial[f.key] = (item[f.key] as string) ?? "";
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const persist = async (key: string, value: string) => {
    if (((item[key] as string) ?? "") === value) return;
    setSaving(true);
    try {
      const { item: updated } = await api.updateCollectionItem(config.path, item.id, {
        [key]: value || null
      });
      onChanged(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const set = (key: string, value: string) => setValues((v) => ({ ...v, [key]: value }));

  return (
    <div className="detail">
      <header className={`detail-head${config.showImage ? " detail-head-portrait" : ""}`}>
        {config.showImage && (
          <PortraitControl
            name={values[config.titleField] ?? ""}
            imageUrl={item.imageData as string | null | undefined}
            onChange={(d) => persist("imageData", d ?? "")}
          />
        )}
        <input
          className="detail-title"
          value={values[config.titleField] ?? ""}
          onChange={(e) => set(config.titleField, e.target.value)}
          onBlur={(e) => persist(config.titleField, e.target.value)}
          placeholder={`${config.singular} name`}
        />
        <div className="row gap-2">
          <span className="detail-saved faint">{saving ? "Saving…" : "Saved"}</span>
          <button className="btn btn-icon btn-ghost btn-danger" title="Delete" onClick={onDelete}>
            <Icon name="trash" size={15} />
          </button>
        </div>
      </header>
      <div className="detail-body">
        <div className="detail-grid">
          {config.fields.map((field) => (
            <DetailField
              key={field.key}
              field={field}
              value={values[field.key] ?? ""}
              refs={refs}
              onChange={(v) => set(field.key, v)}
              onCommit={(v) => persist(field.key, v)}
            />
          ))}
        </div>
        {config.showMentions && <Mentions characterId={item.id} onOpenScene={onOpenScene} />}
      </div>
    </div>
  );
}

function DetailField({
  field,
  value,
  refs,
  onChange,
  onCommit
}: {
  field: FieldSpec;
  value: string;
  refs: CollectionRefs;
  onChange: (v: string) => void;
  onCommit: (v: string) => void;
}) {
  const isWide = field.type === "textarea";
  return (
    <div className={`field ${isWide ? "detail-field-wide" : ""}`}>
      <label className="label">{field.label}</label>
      {field.type === "textarea" ? (
        <textarea
          className="textarea"
          rows={field.rows ?? 3}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
        />
      ) : field.type === "select" ? (
        <select
          className="select"
          value={value}
          onChange={(e) => { onChange(e.target.value); onCommit(e.target.value); }}
        >
          <option value="">—</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{opt[0]!.toUpperCase() + opt.slice(1)}</option>
          ))}
        </select>
      ) : field.type === "scene-ref" ? (
        <select
          className="select"
          value={value}
          onChange={(e) => { onChange(e.target.value); onCommit(e.target.value); }}
        >
          <option value="">—</option>
          {refs.scenes.map((s) => (
            <option key={s.id} value={s.id}>{s.title}</option>
          ))}
        </select>
      ) : field.type === "location-ref" ? (
        <select
          className="select"
          value={value}
          onChange={(e) => { onChange(e.target.value); onCommit(e.target.value); }}
        >
          <option value="">—</option>
          {refs.locations.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      ) : (
        <input
          className="input"
          type={field.type === "date" ? "date" : "text"}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
        />
      )}
      {field.hint && <p className="faint" style={{ fontSize: 10.5, margin: "4px 0 0" }}>{field.hint}</p>}
    </div>
  );
}

function Mentions({ characterId, onOpenScene }: { characterId: string; onOpenScene: (id: string) => void }) {
  const [mentions, setMentions] = useState<CharacterMention[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .characterMentions(characterId)
      .then((res) => !cancelled && setMentions(res.mentions))
      .catch(() => !cancelled && setMentions([]));
    return () => { cancelled = true; };
  }, [characterId]);

  return (
    <div className="mentions">
      <div className="row gap-2" style={{ marginBottom: 8 }}>
        <Icon name="search" size={14} style={{ color: "var(--accent)" }} />
        <span className="label" style={{ margin: 0 }}>Appears in</span>
      </div>
      {mentions === null ? (
        <span className="faint" style={{ fontSize: 12 }}>Scanning manuscript…</span>
      ) : mentions.length === 0 ? (
        <span className="faint" style={{ fontSize: 12 }}>Not yet mentioned by name in any scene.</span>
      ) : (
        <div className="mentions-list">
          {mentions.map((m) => (
            <button key={m.sceneId} className="mention-chip" onClick={() => onOpenScene(m.sceneId)}>
              <Icon name="file" size={12} />
              <span className="truncate">{m.chapterTitle} · {m.sceneTitle}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
